<template>
  <div id="app">
    <component :is="layout">
      <router-view />
    </component>
  </div>
</template>

<style lang="scss">
  @import "@/scss/fonts.scss";
  @import "@/scss/main.scss";
  @import "@/scss/text.scss";
  @import "@/scss/form.scss";
  @import "@/scss/drag.scss";
  @import "@/scss/labels.scss";
  @import "@/scss/statuses.scss";
  @import "@/scss/mobile.scss";
  @import "@/scss/buttons.scss";
  @import "@/scss/activityLog.scss";
  @import "@/scss/tables.scss";
  @import "@/scss/tab.scss";
  @import "@/scss/wizard.scss";
  @import "@/scss/dialog.scss";
  @import "@/scss/public.scss";
  @import "@/scss/layout.scss";
  @import "@/scss/misc.scss";
  @import "@/scss/icons.scss";
  @import "@/scss/messagebox.scss";

  /* Module Specific Styles */
  @import "@/modules/orders/scss/orders.scss";
  @import "@/modules/tasks/scss/tasks.scss";
  @import "@/modules/intelligence/scss/analytics.scss";
  @import "@/modules/production/scss/production.scss";
  @import "@/modules/automations/scss/automations.scss";
  @import "@/modules/inventory/scss/inventory.scss";
</style>

<script>
import semver from 'semver'
import EventBus from '@/utils/eventBus'

const defaultLayout = "Default";

export default {
  name: "App",
  components: {},
  data() {
    return {
      title: "Title",
      currentUserId: null
    };
  },
  computed: {
    layout() {
      if(Object.keys(this.$route.meta).length == 0)
        return 'LoadingLayout'
      return (this.$route.meta.layout || defaultLayout) + "Layout"
    }
  },
  mounted() {
    EventBus.on(EventBus.EventTypes.userSessionUpdated, this.userSessionUpdated)
    EventBus.on(EventBus.EventTypes.accountUpdated, this.accountUpdated)
    this.currentUserId = this.$authService.getUserId()

    this.requestData()
    this.subscribeEndpoint('/profile/' + this.currentUserId, () => {
      this.requestData()
    })
    this.subscribeEndpoint('/profile/menu', () => {
      this.requestMenuData()
    })
    this.subscribeEndpoint('/communication/notifications', () => {
      this.requestNotificationData()
    })

    // Check changelog status for the alert notification
    this.checkChangeLogStatus()
  },
  beforeDestroy() {
    EventBus.off(EventBus.EventTypes.userSessionUpdated, this.userSessionUpdated)
    EventBus.off(EventBus.EventTypes.accountUpdated, this.accountUpdated)
    this.unsubscribeEndpoint('/profile/' + this.currentUserId)
    this.unsubscribeEndpoint('/profile/menu')
    this.unsubscribeEndpoint('/communication/notifications')
  },
  methods: {
    onLogout(event) {
      if (event) event.preventDefault();

      localStorage.removeItem("jwt");
      this.$router.push("/login");
    },
    requestData() {
      if(this.$authService.hasAuthSession()) {
        this.$api.get("/profile").then(response => {
          this.$store.commit("profile/setProfile", response.data);

          this.checkAppMinVersion(response.data)
        })
        this.$apiService.adminService.getAccount().then(response => {
          this.$store.commit("account/setAccountData", response.data)
        })
      }
    },
    requestNotificationData() {
      if(this.$authService.hasAuthSession()) {
        this.$apiService.communicationService.getNotificationSummary()
        .then(response => {
          this.$store.commit("profile/setNotificationSummary", response.data);
        })
      }
    },
    requestMenuData() {
      if(this.$authService.hasAuthSession()) {
        this.$apiService.coreService.getProfileMenu()
        .then(response => {
          this.$store.commit("profile/setMenuList", response.data);
        })
      }
    },
    userSessionUpdated() {
      this.unsubscribeEndpoint('/profile/' + this.currentUserId)
      this.unsubscribeEndpoint('/profile/menu')
      this.unsubscribeEndpoint('/communication/notifications')

      this.currentUserId = this.$authService.getUserId()

      if(this.currentUserId) {
        this.requestData()
        
        this.subscribeEndpoint('/profile/' + this.currentUserId, () => {
          this.requestData()
        })
        this.subscribeEndpoint('/profile/menu', () => {
          this.requestMenuData()
        })
        this.subscribeEndpoint('/communication/notifications', () => {
          this.requestNotificationData()
        })
      }
    },
    accountUpdated() {
      this.requestData()
    },
    checkChangeLogStatus() {
      let changeLogUpdatedAlert = true
      let currentVersion = process.env.VERSION

      let lastViewedBuildVersion = localStorage.getItem('app-changelogview-lastbuild')
      if(lastViewedBuildVersion == currentVersion)
        changeLogUpdatedAlert = false

      this.$store.commit('profile/setChangeLogUpdated', changeLogUpdatedAlert)
    },
    checkAppMinVersion(profileData) {
      var minVersionWarning = false
      let currentVersion = process.env.VERSION

      // If we require a minimum version, check that
      if(profileData.ui && profileData.ui.minVersion && currentVersion) {
        if(semver.valid(profileData.ui.minVersion) && semver.valid(currentVersion)) {

          if(semver.lt(currentVersion, profileData.ui.minVersion)) {

            minVersionWarning = true
          }
        }
      }

      this.$store.commit('system/setVersionUpdateRequired', minVersionWarning)
    }
  }
};
</script>
